import { driver, DriveStep } from 'driver.js'
import i18n from 'i18next'
import { Classes } from 'jss'

import endUserManual from '../../assets/ai-go-studio-end-user-manual.pdf'
import { Settings } from '../../components/Settings/types'
import { ProductType } from '../../types'

const TITLE_DRIVER = i18n.t('Images')

const getDriver = (
  classesDriver: Classes<'driver'>,
  settings: Settings,
  updateSettings: (settings: Partial<Settings>) => void,
  updatePreference: () => void,
  productType: ProductType
) => {
  const driverSteps: DriveStep[] = [
    {
      popover: {
        title: i18n.t('Welcome to AI-go Studio!'),
        description: `
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css">          
          ${i18n.t(
            'This guided tour will help you understand the main functionalities of AI-go Studio.'
          )}
          <br/><br/>
          ${i18n.t('You can always access the guided tour by clicking on the')}
          <b><i style="font-size:15px" class="fa">&#xf059;</i></b>
          ${i18n.t('icon in the upper right corner.')}
        `,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#asideDivId',
      popover: {
        title: i18n.t('Product details'),
        description: `${i18n.t(
          'Here you can find an overview of the product details, such as'
        )}:<br/>
        <ul style="padding-left:15px;">
            <li>${i18n.t('Name')}</li>
            <li>${i18n.t('Description')}</li>
            <li>${i18n.t('Date and time of creation')}</li>
            <li>${i18n.t(
              'Model type (classification, anomaly, segmentation, Patch)'
            )}</li>
            <li>${i18n.t('Classes')}</li>
            <li>${i18n.t('Labels')}</li>
            <li>${i18n.t('Number of uploaded images')}</li>
        </ul>`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#imagesButtonId',
      popover: {
        title: TITLE_DRIVER,
        description: i18n.t('Click here to manage the images of the dataset.'),
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#divHeaderImagesId',
      popover: {
        title: TITLE_DRIVER,
        description: `${i18n.t(
          'Upload images to populate the dataset. You can upload images in bulk or one by one.'
        )}<br/>
        ${
          productType === 'segmentation' ||
          productType === 'classification_patch'
            ? `${i18n.t(
                'You can also import custom annotations via zip file with a predefined structure (see details in end user manual).'
              )}<br/><br/>`
            : ''
        }
        ${i18n.t('Images can be sorted, filtered, downloaded, and deleted.')}`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#divImagesId',
      popover: {
        title: TITLE_DRIVER,
        description: `
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css">          
          ${i18n.t(
            'Here you can see all the uploaded images. To annotate an image, hover over it, click the three dots and click'
          )} <b>${i18n.t('Annotate classes')}</b>.
          <br/><br/>
          ${i18n.t('Images with the')} 
          <i style='font-size:15px' class='fa'>&#xf024;</i>
          ${i18n.t(
            'icon are used in one or more training, so they cannot be deleted.'
          )}
          <br/><br/>
          ${i18n.t('Additional information can be found in the')}
          <a target='_blank' href='${endUserManual}'>
            ${i18n.t('End user manual (PDF)')}
          </a>
          <br/><br/>
          <label htmlFor="doNotShowAgainCheckbox">
            <input
              type="checkbox"
              id="doNotShowAgainCheckbox"
              ${settings.preference__show_guided_tour ? '' : 'checked'}
            />
            ${i18n.t('Do not show again')}
          </label>`,
        side: 'bottom',
        align: 'start',
        onPopoverRender: () => {
          document
            .getElementById('doNotShowAgainCheckbox')
            ?.addEventListener('change', () => {
              updateSettings({
                preference__show_guided_tour: !settings.preference__show_guided_tour,
              })
            })
        },
      },
    },
  ]

  const driverObj = driver({
    showProgress: true,
    steps: driverSteps,
    nextBtnText: i18n.t('Next'),
    prevBtnText: i18n.t('Previous'),
    doneBtnText: i18n.t('Done'),
    popoverClass: classesDriver.driver,
    onPopoverRender: (popover) => {
      const skipButton = document.createElement('button')
      skipButton.id = 'skipButton'
      skipButton.innerText = i18n.t('Skip all')
      popover.footerButtons.appendChild(skipButton)
      skipButton.addEventListener('click', () => {
        const stepsLength = driverObj.getConfig('steps')?.length
        if (stepsLength) {
          driverObj.drive(stepsLength - 1)
        }
      })
    },
    onDestroyStarted: () => {
      if (driverObj.isLastStep()) {
        updatePreference()
      }
      driverObj.destroy()
    },
  })

  return driverObj
}

export default getDriver
